/**
 * WHICH MEETING THE VIEWER IS LOOKING AT (#13840).
 *
 * The recording bar is mounted in the root layout, ABOVE the router, so it
 * cannot read a context a route provides and it cannot be handed props by a
 * page. But on a meeting's own page the bar is not app chrome announcing
 * something elsewhere — it is that meeting's record control, and it has to
 * know which meeting that is, whether or not the meeting happens to be the
 * next one on the viewer's calendar.
 *
 * So the page PUBLISHES and the bar SUBSCRIBES, through the same module-store
 * shape `recording-session-store.ts` uses for the same reason. This is not a
 * cache of the meeting: it is the answer to "what is on screen", and it is
 * cleared when the page unmounts, because a stale answer here would point
 * Record at a meeting the viewer navigated away from.
 *
 * ## Client-safe
 *
 * No `server-only`, no Prisma, no DOM. The publisher is a client component
 * rendered by a server component that already holds the meeting.
 */

/**
 * What the bar needs to be that meeting's control.
 *
 * Since #13840 that is the whole of row B's input, not just a name: the bar
 * carries the five Record states the page footer used to
 * (`deriveMeetingRecordPosture`), so it needs the four occurrence timestamps,
 * what has been captured, and whether this viewer may capture at all. All of
 * it is SERVER-READ and published as ISO strings — a module store crosses no
 * serialization boundary, but `Date` instances in a store the bar re-reads on
 * every tick are a needless source of identity churn.
 */
export type MeetingPageContext = {
  meetingId: string
  /**
   * Carried for the capture, never drawn: the meeting-page bar renders no
   * name — the page behind it already is one — but `startRecordingFromBar`
   * files the capture under a title, and the session store shows it in the
   * recording state after the viewer navigates away.
   */
  title: string
  startsAtIso: string | null
  /** Pre-formatted in a zone the server resolved; the browser has none. */
  startsAtLabel: string
  /** `MeetingOccurrence.endsAt` — the booked end, not the real one. */
  endsAtIso: string | null
  scheduledMinutes: number | null
  /** When the meeting actually began, if it has. */
  startedAtIso: string | null
  /** When it actually ended, if it has. */
  endedAtIso: string | null
  /** When the live capture began, or null when nothing is capturing. */
  liveRecordingStartedAtIso: string | null
  /**
   * The live capture's `Recording.id`, beside its start (#15666): the
   * meeting page's in-page "Recording" pill for a viewer who is not the one
   * recording, in this tab, reads it as the note page reads its own.
   */
  liveRecordingId: string | null
  /**
   * That live capture was opened by THIS actor (#16939) — the real one, as
   * for {@link resumableRecordingId}. Absent reads as false. With it, a tab
   * that is capturing nothing knows the server's "live" is a capture that
   * died with another page load, and draws it as interrupted.
   */
  liveRecordingIsActors?: boolean
  /** Audio captured against this meeting, summed over every recording. */
  recordedMs: number
  /** Last note edit or recording activity, as the server saw it. */
  lastActivityAtIso: string | null
  /**
   * THIS ACTOR's interrupted capture on this meeting, if there is one
   * (#14051).
   *
   * Scoped by `createdById` on the server, not merely by org:
   * `resumeRecordingFromBar` refuses another person's id, so offering one here
   * would draw a dead control — and it would disclose that a colleague had
   * been recording this meeting. Null is the ordinary answer, and B4 then
   * offers "Continue recording" rather than "Resume recording".
   *
   * THE ACTOR IS THE REAL ONE (#14052 review). Under impersonation the person
   * being viewed as and the person a `Recording` belongs to are different, and
   * this field follows the second — the identity `getAttributionIdentity()`
   * resolves and the resume action re-scopes on. Publishing the viewed-as
   * person's id here disclosed a colleague's unsettled capture to an admin and
   * drew a control the action then refused.
   *
   * A capture whose chunks are still arriving is deliberately NOT published
   * here: that is B3, and handing its id to a second `MediaRecorder` is the
   * overlapping-chunk-index hazard `recording-bar-menu.ts` describes.
   */
  resumableRecordingId: string | null
  /** Whether the `notetaker` flag admits this viewer to capture. */
  canRecord: boolean
  /**
   * The meeting's calendar event has a video call THIS viewer can see
   * (#15666, the ADR-0064 projection `readMeetingConference` applies). A
   * capture of it defaults to the microphone plus computer audio.
   */
  hasVirtualMeeting: boolean
  /**
   * Whether this viewer passes `requireSectionController` — a section
   * controller or an org admin — the gate `endMeetingFromRecordPosture`
   * enforces (#15104). Resolved by the page; the bar draws Wrap up only when
   * it is true, so nobody is offered an end the server will refuse.
   */
  canEndMeeting: boolean
  /** The VIEWER's zone (ADR-0101), resolved server-side. */
  displayTimezone: string
  /** The instant the server rendered, so the first client tick agrees. */
  renderedAtIso: string
}

function sameContext(
  left: MeetingPageContext,
  right: MeetingPageContext,
): boolean {
  // Field by field rather than a JSON compare: the store is read by
  // `useSyncExternalStore`, which re-renders app-wide chrome on every
  // identity change, and a page that re-renders publishes the same answer
  // again on each one.
  return (
    left.meetingId === right.meetingId &&
    left.title === right.title &&
    left.startsAtIso === right.startsAtIso &&
    left.startsAtLabel === right.startsAtLabel &&
    left.endsAtIso === right.endsAtIso &&
    left.scheduledMinutes === right.scheduledMinutes &&
    left.startedAtIso === right.startedAtIso &&
    left.endedAtIso === right.endedAtIso &&
    left.liveRecordingStartedAtIso === right.liveRecordingStartedAtIso &&
    left.liveRecordingId === right.liveRecordingId &&
    (left.liveRecordingIsActors ?? false) ===
      (right.liveRecordingIsActors ?? false) &&
    left.recordedMs === right.recordedMs &&
    left.lastActivityAtIso === right.lastActivityAtIso &&
    // #14051. Omitting this field would make the bar keep drawing "Continue
    // recording" after a server read discovered the orphan, because every
    // other field is identical between the two answers.
    left.resumableRecordingId === right.resumableRecordingId &&
    left.canRecord === right.canRecord &&
    left.hasVirtualMeeting === right.hasVirtualMeeting &&
    left.canEndMeeting === right.canEndMeeting &&
    left.displayTimezone === right.displayTimezone
    // `renderedAtIso` is DELIBERATELY NOT COMPARED (#13840 review,
    // suggestion 15). `record-posture-slot.tsx` stamps `new Date()` on every
    // server render, so including it made every re-render a "different"
    // context and emitted to app-wide chrome — defeating the field-by-field
    // compare this function exists to be. It is a tie-breaker for the first
    // client tick, not part of the identity: two contexts that agree on every
    // field above describe the same meeting in the same state, whichever
    // instant the server happened to render them.
  )
}

let current: MeetingPageContext | null = null
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

export function subscribeToMeetingPageContext(
  listener: () => void,
): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getMeetingPageContext(): MeetingPageContext | null {
  return current
}

/**
 * The server snapshot, which is always `null`.
 *
 * A server render has no page mounted yet, and returning anything else would
 * make `useSyncExternalStore` hydrate a bar the first client paint disagrees
 * with.
 */
export function getServerMeetingPageContext(): MeetingPageContext | null {
  return null
}

export function publishMeetingPageContext(next: MeetingPageContext): void {
  if (current !== null && sameContext(current, next)) {
    // Same answer. Emitting anyway would re-render app-wide chrome on every
    // re-render of the page that publishes it.
    return
  }
  current = next
  emit()
}

/**
 * Clear the answer, but only if it is still the one this caller published.
 *
 * A client-side navigation from one meeting to another mounts the next page's
 * publisher BEFORE the previous page's cleanup runs. An unconditional clear
 * would therefore delete the new meeting's context moments after it arrived,
 * and the bar would fall back to app chrome on a page that is very much a
 * meeting.
 */
export function clearMeetingPageContext(meetingId: string): void {
  if (current?.meetingId !== meetingId) return
  current = null
  emit()
}

/** Test-only: drop everything, so one case cannot leak into the next. */
export function resetMeetingPageContext(): void {
  current = null
  emit()
}
